/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { countAuthStatusFilters, IAuthServiceStatusSearchFilters } from 'app/um/services/auth-service';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvent } from 'app/shared/lib/fire-events';
import { SetFocusEvent } from 'app/shared/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import { debounce } from 'app/shared/lib/decorators/debounce';
import { deepClone } from 'app/shared/lib/utilities';
import { DeterminationOptionsLong, FlagOptions } from 'app/um/components/auth-statuses/statuses';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';

@autoinject
export class ServiceStatusFilters extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public searchFilters: IAuthServiceStatusSearchFilters = {};
    protected tempSearchFilters: IAuthServiceStatusSearchFilters = {
        determination:{
            statuses: []
        },
        status: '',
        transitions: []
    };

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    private flagOptions: Array<string> = [];

    private conjunction: string = '';

    protected selectedSystemFlags: string[] = [];
    protected selectedSystemFlagsDisplay: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected statuses: UM.AuthServiceStatus[] = [];
    protected selectedTransitionsDisplay: string | null = null;
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected determinationStatuses: UM.AuthStatus[] = [];
    protected availableDeterminationStatuses: UM.AuthStatus[] = [];
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected determinationOptions: string[] = [];
    protected selectedDetermination: string | null = null;
    protected selectedDeterminationDisplay: string | null = null;
    protected selectedDeterminationStatusesDisplay: string | null = null;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    private $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

        this.conjunction = this.i18n.tr('ui:lists.conjunctions.or');
    }

    protected bind(): void {
        this.determinationOptions = <string[]>Object.values(DeterminationOptionsLong);
    }

    ////****************** Events ***************************************************////
    protected determinationChanged(): void {
        const determinationResult = this.selectedDetermination?.split(', ')[1];
        if(this.tempSearchFilters.determination) this.tempSearchFilters.determination.statuses = [];
        this.availableDeterminationStatuses = this.determinationStatuses.filter(ds => ds.determination?.result === determinationResult);
    }

    protected handleSubmit(): void {
        if(!this.state.saving && !this.state.loading) this.search();
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    ////****************** Functions ************************************************////
    protected collapseBoth(): void {
        this.$collapsible.close(0);
        this.$collapsible.close(1);
    }

    protected showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    protected showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected buildFilterDisplayString(strings: string[]): string {
        let returnedString = '';
        if(strings.length === 1) returnedString = strings[0];
        if(strings.length === 2) returnedString = strings.join(this.conjunction);
        if(strings.length > 2) returnedString = strings.slice(0, -1).join(', ') + `,${this.conjunction}` + strings[strings.length - 1];
        return returnedString;
    }

    protected search(): void {
        this.searchFilters = deepClone(this.tempSearchFilters);
        if(this.selectedSystemFlags && this.selectedSystemFlags.length > 0) {
            this.searchFilters.multiFlagFilter = true;
            if(this.selectedSystemFlags.includes(FlagOptions.isApproval)) this.searchFilters.isApproval = true;
            if(this.selectedSystemFlags.includes(FlagOptions.isOpen)) this.searchFilters.isOpen = true;
            if(this.selectedSystemFlags.includes(FlagOptions.isTurnAroundTimeBase)) this.searchFilters.isTurnAroundTimeBase = true;
            if(this.selectedSystemFlags.includes(FlagOptions.isVoided)) this.searchFilters.isVoided = true;
            if(this.selectedSystemFlags.includes(FlagOptions.sendToClaimsSystem)) this.searchFilters.sendToClaimsSystem = true;
            this.selectedSystemFlagsDisplay = this.buildFilterDisplayString(this.selectedSystemFlags);
        } else {
            this.selectedSystemFlagsDisplay = null;
        }

        if(this.searchFilters.determination?.statuses && this.searchFilters.determination.statuses.length > 0) {
            const determinationStatusDisplays: string[] = this.searchFilters.determination.statuses.map(ds => ds.$display || '');
            this.selectedDeterminationStatusesDisplay = this.buildFilterDisplayString(determinationStatusDisplays);
        } else {
            this.selectedDeterminationStatusesDisplay = null;
        }

        if(this.searchFilters.transitions && this.searchFilters.transitions.length > 0) {
            const transitionDisplays: string[] = this.searchFilters.transitions.map(t => t.$display || t.description || '');
            this.selectedTransitionsDisplay = this.buildFilterDisplayString(transitionDisplays);
        } else {
            this.selectedTransitionsDisplay = null;
        }

        if(this.selectedDetermination) {
            if(!this.searchFilters?.determination) this.searchFilters.determination = {};
            if(!this.searchFilters?.determination?.result) this.searchFilters.determination.result = {};
            switch(this.selectedDetermination) {
                case DeterminationOptionsLong.Approved:
                    this.searchFilters.determination.result.approved = true;
                    break;
                case DeterminationOptionsLong.Denied:
                    this.searchFilters.determination.result.denied = true;
                    break;
                case DeterminationOptionsLong.Modified:
                    this.searchFilters.determination.result.modified = true;
                    break;
                case DeterminationOptionsLong.Other:
                    this.searchFilters.determination.result.other = true;
                    break;
            }
            this.selectedDeterminationDisplay = this.selectedDetermination;
        } else {
            this.selectedDeterminationDisplay = null;
        }


        const authStatusFilterCount = countAuthStatusFilters(this.searchFilters);
        this.fireSearch();
        if(authStatusFilterCount === 0) {
            this.$collapsible.close(0);
        } else {
            this.showSelectedCriteria();
        }
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    // debounce gives the bindings time to reach the parent component
    @debounce(0)
    protected fireSearch(): void {
        fireEvent(this.$element, 'search');
    }

    protected clear(returnFocus?: boolean, andCollapse?: boolean): void {
        this.selectedTransitionsDisplay = null;
        this.selectedSystemFlags.splice(0);
        this.selectedSystemFlagsDisplay = null;
        this.selectedDetermination = null;
        this.selectedDeterminationDisplay = null;
        this.selectedDeterminationStatusesDisplay = null;
        this.tempSearchFilters = {
            determination: {
                statuses: []
            },
            status: '',
            transitions: []
        };
        if(andCollapse) this.collapseBoth();
        else this.showAvailableCriteria();

        fireEvent(this.$element, 'clear-results');

        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }

    ////****************** Service Calls ********************************************////

}
